/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { Typography } from '@mui/material'
import { useForm } from 'react-final-form'
import TabsNavigation from 'components/TabsNavigation'
import { EditorField } from 'components/Editor'
import { InputField, NumberInputField } from 'components/Input'
import { SelectField } from 'components/Select'
import {
  validateRequired,
  validateRequiredDefaultHtml,
  validateRequiredHtml,
  validateRequiredNumber,
} from 'utils/validation'
import {
  PassageSubCategoryEnum,
  PassageTypeEnum,
  PracticeTestTypeEnum,
  QuestionInvertTypeEnum,
} from '__generatedTypes__/globalTypes'
import { validateVideoLink } from 'pagesNew/ResponseTests/Form/utils'
import InvertImagesField from 'pagesNew/Questions/Form/InvertImagesField'
import * as classes from './styles'
import { PASSAGE_CATEGORIES } from './consts'

export type Passage = {
  id?: string
  title?: string
  startQuestion?: number
  endQuestion?: number
  content?: string
  invertContentImages?: QuestionInvertTypeEnum
  category?: { id: PassageTypeEnum; title: string } | null
  subCategory?: { id: PassageSubCategoryEnum; title: string } | null
  reference?: string | null
  watchPassageWalkthrough?: string | null
}

export const isCarsPT = (type: PracticeTestTypeEnum) =>
  [
    PracticeTestTypeEnum.CarsPracticeTest,
    PracticeTestTypeEnum.CarsPracticePassage,
    PracticeTestTypeEnum.DailyCarsPractice,
  ].includes(type)

const PASSAGES_COUNT: Partial<Record<PracticeTestTypeEnum, number>> = {
  [PracticeTestTypeEnum.ReadingQuestionBank]: 1,
  [PracticeTestTypeEnum.ReadingPracticeTest]: 3,
  [PracticeTestTypeEnum.ReadingMiniTest]: 1,
  [PracticeTestTypeEnum.CarsPracticeTest]: 9,
  [PracticeTestTypeEnum.CarsPracticePassage]: 1,
  [PracticeTestTypeEnum.DailyCarsPractice]: 1,
}

export const isPassagesAvailable = (type: PracticeTestTypeEnum) => Object.keys(PASSAGES_COUNT).includes(type)

export const getInitialPassages = (type: PracticeTestTypeEnum): Passage[] => {
  const passagesCount = PASSAGES_COUNT[type] ?? 0

  if (!passagesCount) {
    return []
  }

  return Array.from({ length: passagesCount }, (_, index) => ({
    invertContentImages: QuestionInvertTypeEnum.AUTO,
    startQuestion: index === 0 ? 1 : undefined,
  }))
}

export type PassagesFieldArrayProps = {
  type: PracticeTestTypeEnum
  fields: FieldArrayRenderProps<Passage, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Passage, HTMLElement>['meta']
}

export const PassagesFieldArray = ({ type, fields, meta }: PassagesFieldArrayProps) => {
  const form = useForm()
  const [selected, setSelected] = useState<string>('0')
  const isCars = isCarsPT(type)

  return (
    <>
      <div>
        <Typography variant="body2" css={classes.label}>
          Passage #
        </Typography>
        <TabsNavigation
          tabs={fields.map((field, index) => ({
            id: String(index),
            title: String(index + 1),
            error: !!meta.touched && !!meta.error?.[index],
          }))}
          value={{ id: String(selected), title: String(selected + 1), error: false }}
          onChange={({ id }) => setSelected(id)}
        />
      </div>
      <div>
        {fields.map((field, index) => (
          <div key={field} css={classes.tabContent(+selected === index)}>
            <InputField label="Passage Title" name={`${field}.title`} validate={validateRequired} fullWidth />
            <div css={classes.columns}>
              <NumberInputField
                label="Start Question"
                name={`${field}.startQuestion`}
                validate={validateRequiredNumber}
                error={meta.error?.[index]?.startQuestion}
                disabled={!+selected}
                fullWidth
              />
              <NumberInputField
                label="End Question"
                name={`${field}.endQuestion`}
                validate={validateRequiredNumber}
                error={meta.error?.[index]?.endQuestion}
                disabled={+selected === fields.value.length - 1}
                fullWidth
              />
            </div>
            {isCars && (
              <>
                <SelectField
                  name={`${field}.category`}
                  validate={validateRequired}
                  items={Object.values(PASSAGE_CATEGORIES)}
                  onChange={() => form.change(`${field}.subCategory`, null)}
                  label="Category"
                  placeholder="Choose Category"
                  fullWidth
                />
                <SelectField
                  name={`${field}.subCategory`}
                  validate={validateRequired}
                  items={
                    PASSAGE_CATEGORIES[fields.value?.[index]?.category?.id as PassageTypeEnum]?.subCategories ?? []
                  }
                  label="Sub-Category"
                  placeholder="Choose Sub-Category"
                  fullWidth
                />
              </>
            )}
            <EditorField
              type="passage"
              label="Passage Content"
              name={`${field}.content`}
              placeholder="Enter Passage Content"
              validate={validateRequiredHtml}
            />
            {!isCars && (
              <InvertImagesField
                name={`${field}.invertContentImages`}
                title="Passage"
                validate={validateRequired}
                htmlWithImagesForPreview={fields.value?.[index]?.content}
              />
            )}
            {isCars && (
              <>
                <EditorField
                  type="default"
                  label="Reference"
                  name={`${field}.reference`}
                  placeholder="Enter Reference"
                  validate={validateRequiredDefaultHtml}
                />
                <InputField
                  label="Passage Walkthrough (Optional)"
                  name={`${field}.watchPassageWalkthrough`}
                  validate={validateVideoLink}
                  placeholder="Enter Video Link"
                  fullWidth
                />
              </>
            )}
          </div>
        ))}
      </div>
    </>
  )
}
